"use client";

import { Box, Dialog } from "@mui/material";
import React, { useState } from "react";
import { NeedMoreInfo as NeedMoreInfoTypes } from "@/types/dto/common/need-more-info";
import ContactInfoForm from "@/components/common-controls/collette-forms/contact-info-form";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import useScrollLock from "@/hooks/scroll-lock/use-scroll-lock";

import { SectionHeader } from "../section-header";

interface NeedMoreInfoProps {
  needMoreInfoData: NeedMoreInfoTypes;
  moduleRankingPosition?: string | number;
  parentContainerType?: string;
}

const NeedMoreInfo: React.FC<NeedMoreInfoProps> = ({
  needMoreInfoData,
  moduleRankingPosition,
  parentContainerType = "form"
}) => {
  const { basicInfo, hubSpotFormInfo, isFullWidth = false, customBg } = needMoreInfoData || {};
  const hasCustomBg = Boolean(customBg);
  const [openForm, setOpenForm] = useState(false);

  // Lock scroll when form is open
  useScrollLock(openForm);

  return (
    <Box
      component="section"
      data-track="visibility"
      data-testid="need-more-info-module"
      data-parent-container-type={parentContainerType}
      data-module-name="Need More Info Section"
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
      className={isFullWidth ? "fluid-box" : "full-width-container"}
      sx={{
        ...(hasCustomBg
          ? { p: { xs: "4rem 2.4rem", sm: "4rem 3.2rem", lg: "6rem 10rem" } }
          : { px: { xs: "2.4rem", md: "3.2rem" }, pt: "0", pb: "4rem" }),
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        backgroundColor: hasCustomBg ? "primary.main" : undefined,
        backgroundImage: hasCustomBg
          ? "url('https://images.ctfassets.net/69jewsgul74b/2C2zTjH8GZVpuv4hc4pLWo/b4b28512dd9707c16674979bc60ee3ec/footeremailpattern.png'), url('/images/Pattern.svg')"
          : undefined,
        backgroundRepeat: hasCustomBg ? "repeat, repeat" : undefined,
        backgroundPosition: hasCustomBg ? "center, center" : undefined,
        alignItems: "center"
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: hasCustomBg ? "30px" : "32px",
            bgcolor: hasCustomBg ? "transparent" : "background.brand",
            p: hasCustomBg ? undefined : "2.4rem",
            borderRadius: hasCustomBg ? "0" : "20px",
            width: "100%"
          }}
        >
          <SectionHeader
            headerText={basicInfo?.headerText}
            copyText={basicInfo?.copyText}
            headerTestId="need-more-info-module-header"
            copyTestId="need-more-info-module-subheader"
            headerSx={{
              fontSize: { xs: "2.4rem", md: "2.6rem", lg: "2.8rem" },
              fontWeight: "600",
              textAlign: "center",
              color: "white",
              textTransform: "none!important"
            }}
            copySx={{
              textAlign: "center",
              color: "white",
              fontSize: { xs: "1.6rem", md: "1.8rem" },
              margin: "0px !important",
              "& p": { margin: "0px !important" }
            }}
            sx={{
              textAlign: "center",
              alignSelf: "center",
              gap: "10px"
            }}
          />
          <ColletteButton
            onClick={() => setOpenForm(true)}
            label={basicInfo?.callToActionText || "Request a Callback"}
            data-testid="request-callback-button"
            testId="request-callback-button"
            data-track="click"
            variant="outlined"
            size="large"
            sx={{
              alignSelf: "center",
              bgcolor: "white"
            }}
          />
        </Box>
      </Box>
      <Dialog open={openForm} onClose={() => setOpenForm(false)}>
        <ContactInfoForm formData={hubSpotFormInfo} showForm={setOpenForm} />
      </Dialog>
    </Box>
  );
};

export default NeedMoreInfo;
